import * as React from 'react';
import { ReferenceText } from '../../../../../../utils/styled';
import FormRow from 'Components/FormRow';
import InputHandler from 'Components/InputHandler';
import { useIntl } from 'react-intl';
import FormSection from 'Components/FormSection';
import colors from '../../../../../../../../config/theme/colors';
import { InputlessFormRow } from 'Routes/Diagnosis/Document/components';
import { msDiagnosticCriteria } from 'neuro-schemas';

const fmPrefix = 'diagnosis.msDiagnosticCriteria.mcDonald2024DiagnosticCriteria';

const McDonald2024 = ({ formData, editingID }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();
  const fm = (id: string): string => formatMessage({ id });

  return (
    <FormSection>
      <div style={{ color: colors.primary, fontSize: '2rem', fontWeight: 600, marginBottom: '1rem' }}>
        {fm(`${fmPrefix}.title`)}
      </div>
      <FormRow title={fmPrefix + '.presentationType'}>
        <InputHandler
          name="presentationType"
          type="Radio"
          options={['relapse', 'progression', 'radiologicalFinding', 'untypicalSymptom']}
          optionFormatter={o => fm(`${fmPrefix}.opts.${o}`)}
          editing={!!editingID}
          formData={formData}
          dependentFieldsList={(val, prevVal) => {
            if ((val === 'relapse' && prevVal === 'progression') || (val === 'progression' && prevVal === 'relapse')) {
              return ['spinalLesionsTwoOrMore'];
            }
            return ['spinalLesionsTwoOrMore', 'prlOneOrMore'];
          }}
          style={{ fontWeight: 400 }}
        />
      </FormRow>
      <FormRow title={fmPrefix + '.etiologiesExcluded'} condition={!!formData.document.presentationType}>
        <InputHandler
          name="etiologiesExcluded"
          type="RadioButtonRow"
          preset="yesNoUnknown"
          height={3.5}
          width={9}
          editing={!!editingID}
          formData={formData}
          dependentFieldsList={() => ['typicalLocationCount', 'ocbPositive', 'cvsSixOrMore', 'temporalDissemination']}
        />
      </FormRow>
      <FormRow
        title={fmPrefix + '.typicalLocationCount'}
        description={
          <>
            {formatMessage(
              { id: `${fmPrefix}.typicalLocationCountInfo` },
              {
                ul: chunks => <ul key={chunks.join('')}>{React.Children.toArray(chunks)}</ul>,
                li: chunks => <li key={chunks.join('')}>{React.Children.toArray(chunks)}</li>,
              },
            )}
          </>
        }
        condition={formData.document.etiologiesExcluded === 'yes'}
      >
        <InputHandler
          name="typicalLocationCount"
          type="Radio"
          options={['0', '1', '2-3', '4+', 'unknown']}
          optionFormatter={o => {
            if (o === 'unknown') return fm(`general.${o}`);
            if (o === '4+') return fm(`${fmPrefix}.opts.${o}`);
            return `${o}`;
          }}
          editing={!!editingID}
          formData={formData}
          dependentFieldsList={value => {
            if (value === '1') return [];
            if (value === 'unknown') {
              return ['prlOneOrMore'];
            }
            return ['prlOneOrMore', 'spinalLesionsTwoOrMore'];
          }}
          style={{ fontWeight: 400 }}
        />
      </FormRow>
      <FormRow
        title={fmPrefix + '.spinalLesionsTwoOrMore'}
        condition={
          formData.document.presentationType === 'progression' &&
          (formData.document.typicalLocationCount === '1' || formData.document.typicalLocationCount === 'unknown')
        }
      >
        <InputHandler
          name="spinalLesionsTwoOrMore"
          type="RadioButtonRow"
          preset="yesNoUnknown"
          height={3.5}
          width={9}
          editing={!!editingID}
          formData={formData}
        />
      </FormRow>
      <FormRow
        title={fmPrefix + '.ocbPositive'}
        condition={!!(formData.document.presentationType && formData.document.etiologiesExcluded === 'yes')}
      >
        <InputHandler
          name="ocbPositive"
          type="RadioButtonRow"
          preset="yesNoUnknown"
          optionFormatter={o => {
            if (o === 'unknown') return fm(`${fmPrefix}.opts.unknownV2`);
            return fm(`general.${o}`);
          }}
          height={3.5}
          width={9}
          editing={!!editingID}
          formData={formData}
        />
      </FormRow>
      <FormRow
        title={fmPrefix + '.cvsSixOrMore'}
        condition={!!(formData.document.presentationType && formData.document.etiologiesExcluded === 'yes')}
      >
        <InputHandler
          name="cvsSixOrMore"
          type="RadioButtonRow"
          preset="yesNoUnknown"
          optionFormatter={o => {
            if (o === 'unknown') return fm(`${fmPrefix}.opts.unknownV3`);
            return fm(`general.${o}`);
          }}
          height={3.5}
          width={9}
          editing={!!editingID}
          formData={formData}
        />
      </FormRow>
      <FormRow
        title={fmPrefix + '.temporalDissemination'}
        condition={!!(formData.document.presentationType && formData.document.etiologiesExcluded === 'yes')}
      >
        <InputHandler
          name="temporalDissemination"
          type="RadioButtonRow"
          preset="yesNoUnknown"
          height={3.5}
          width={9}
          editing={!!editingID}
          formData={formData}
        />
      </FormRow>
      <FormRow
        title={fmPrefix + '.prlOneOrMore'}
        condition={
          !!(
            (formData.document.presentationType === 'progression' ||
              formData.document.presentationType === 'relapse') &&
            formData.document.typicalLocationCount === '1'
          )
        }
      >
        <InputHandler
          name="prlOneOrMore"
          type="RadioButtonRow"
          preset="yesNoUnknown"
          optionFormatter={o => {
            if (o === 'unknown') return fm(`${fmPrefix}.opts.unknownV3`);
            return fm(`general.${o}`);
          }}
          height={3.5}
          width={9}
          editing={!!editingID}
          formData={formData}
        />
      </FormRow>
      <InputlessFormRow
        title={'diagnosis.msDiagnosticCriteria.history.criteriasAreFullfilled'}
        element={
          <>
            {fm(
              `diagnosis.msDiagnosticCriteria.mcDonald2024DiagnosticCriteria.results.${msDiagnosticCriteria.utils.mcDonald2024CriteriaMet(formData.document) ?? 'insufficientInfo'}`,
            )}
          </>
        }
        boldValue={true}
      />
      <ReferenceText>
        MS-tauti: Käypä hoito -suositus (Julkaistu: 11.03.2026). Suomalaisen Lääkäriseuran Duodecimin ja Suomen
        Neurologinen Yhdistys ry:n asettama työryhmä. Helsinki: Suomalainen Lääkäriseura Duodecim, 2026 (viitattu:
        07.04.2026). Saatavilla Internetissä: www.kaypahoito.fi
      </ReferenceText>
    </FormSection>
  );
};

interface IOwnProps {
  documents?: Array<IMSCriteria>;
  formData: IFormData<IMSCriteria>;
  view?: IView;
  editingID?: string;
}

export default McDonald2024;
